import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faTwitter } from "@fortawesome/free-brands-svg-icons";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { db } from "@lib/firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";

const parsePrice = (price) => {
  if (!price) return 0;
  if (typeof price === "number") return price;
  const str = String(price).toUpperCase().trim();
  if (str.includes("M")) return Number(str.replace("M", "")) * 1000000;
  if (str.includes("K")) return Number(str.replace("K", "")) * 1000;
  return Number(str.replace(/[^0-9.]/g, "")) || 0;
};

const socialMedia = [
  { icon: faFacebook, link: "#" },
  { icon: faInstagram, link: "https://www.instagram.com/jotr_gepang" },
  { icon: faTwitter, link: "#" },
];

export const RoadToJOTR = () => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventRef = doc(db, "semuaKegiatan", "ROADTOJOTR2026");
        const eventDoc = await getDoc(eventRef);
        if (eventDoc.exists()) {
          setEvent(eventDoc.data());
        } else {
          setError("Road to JOTR event not found");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, []);

  useEffect(() => {
    if (!user) return;
    const checkRegistration = async () => {
      try {
        const q = query(
          collection(db, "pendaftaranRoadToJOTR2026"),
          where("uid", "==", user.uid)
        );
        const snap = await getDocs(q);
        const active = snap.docs
          .map(d => d.data())
          .find(d => d.administration_status !== "rejected" && d.transaction_status !== "expire" && d.transaction_status !== "cancel");
        if (active) setExistingRegistration(active);
      } catch (err) {
        console.warn("[RoadToJOTR] Failed to check registration:", err.message);
      }
    };
    checkRegistration();
  }, [user]);

  const handleRegister = (category) => {
    if (!user) {
      showNotification("info", "Please login first to register.");
      navigate("/login");
      return;
    }
    if (existingRegistration) {
      navigate("/myregistration");
      return;
    }
    navigate(`/form/${event.slug}`, {
      state: {
        title: `${event.title} - ${category.categoryName}`,
        price: category.price,
        foreignerPrice: category.foreignerPrice || category.price,
        eventId: event.eventId,
        categoryName: category.categoryName,
        collectionName: event.collectionName || `pendaftaran${event.eventId}`,
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lime-600"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center bg-white p-4">
        <h1 className="text-4xl font-bold text-lime-600 mb-4">Error</h1>
        <p className="text-gray-600 mb-8">{error || "Event not found"}</p>
        <Link to="/" className="text-lime-600 underline">Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white">
      {/* ── Hero ── matches SectionHero design exactly ── */}
      <section
        id="hero"
        className="w-full -mt-20 pt-20 min-h-screen text-black text-center flex flex-col justify-center relative"
        style={{
          backgroundImage: "url('/bg/bghero11.jpg')",
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 z-0"></div>

        {/* Desktop: Social media icons */}
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-0 hidden md:flex flex-col items-center gap-4">
          {socialMedia.map((social, index) => (
            <motion.a
              key={index}
              href={social.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white text-xl sm:text-2xl bg-opacity-50 w-8 h-8 flex items-center justify-center rounded-full hover:text-lime-200 transition-colors"
              initial={{ opacity: 0, y: -15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 + index * 0.1, ease: [0.4, 0, 0.2, 1] }}
              viewport={{ once: true }}
            >
              <FontAwesomeIcon icon={social.icon} />
            </motion.a>
          ))}
        </div>

        <div className="relative text-white text-center px-6 z-1 flex flex-col justify-center flex-grow mt-2 md:mt-24 md:pb-24 gap-2">
          <motion.img
            src="jotrWhite.svg"
            alt="JOTR Logo"
            className="h-36 md:h-52 mx-auto object-contain"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <motion.p
            className="text-2xl md:text-5xl lg:text-6xl pt-4 font-bold mx-auto text-white"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            ROAD TO JOTR 2026
          </motion.p>
          <motion.p
            className="text-sm md:text-4xl lg:text-3xl pb-10 font-normal mx-auto text-white"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            TECHNICAL OUTDOOR, SENTUL
          </motion.p>
          <motion.h2
            className="text-sm sm:text-lg md:text-lg font-normal uppercase text-white"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            #JOTRSEATRC2026
          </motion.h2>
        </div>

        {/* Scroll button */}
        <div className="absolute bottom-8 left-0 w-full flex justify-center z-0">
          <motion.div
            className="cursor-pointer text-white animate-bounce z-0"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            onClick={() => {
              const next = document.getElementById("categories");
              if (next) next.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-4xl" />
          </motion.div>
        </div>
      </section>

      {/* ── Categories Section ── matches SectionKategori card design ── */}
      <section
        id="categories"
        className="w-full py-20 relative z-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: "url('/bg/bghero11.jpg')" }}
      >
        <div className="absolute inset-0 bg-lime-50/90 z-0"></div>
        <div className="container mx-auto px-2 sm:px-4 lg:px-6 relative z-10">
          <div className="max-w-[1440px] mx-auto">
            <div className="text-center mb-8">
              <motion.h2
                className="text-2xl font-black uppercase tracking-wider text-lime-600"
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                CHOOSE YOUR CATEGORIES
              </motion.h2>
             
            </div>

            <div className="flex flex-col gap-4 py-10 px-2 sm:flex-row sm:flex-nowrap sm:justify-center sm:px-4 lg:px-6">
              {event.categories.map((cat, idx) => (
                <div
                  key={cat.categoryName}
                  className={`group relative z-0 w-full sm:min-w-[240px] sm:flex-1 sm:flex-shrink-0 ${existingRegistration ? "cursor-default" : "cursor-pointer"}`}
                  onClick={() => handleRegister(cat)}
                >
                  <motion.div
                    className="relative rounded-2xl overflow-hidden bg-cover bg-center w-full h-72 z-0"
                    style={{
                      backgroundImage: `url(${idx === 0 ? "/card/10k.jpg" : "/card/21k.jpg"})`,
                      aspectRatio: "2/1",
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1, ease: "easeOut" }}
                  >
                    <div className="relative flex flex-col items-center justify-center h-full bg-gradient-to-t from-lime-600/100 to-lime-600/20 gap-4 transition-all duration-300 group-hover:opacity-0 z-0">
                      <div className="flex flex-col items-center">
                        <h3 className="text-3xl font-black mt-2 mb-1 text-white uppercase z-10 text-center">
                          {cat.distance} KM
                        </h3>
                        <p className="text-xs font-bold text-white uppercase text-center">
                          {cat.categoryName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-center">
                          <p className="text-xs text-white/80">ELEVATION</p>
                          <p className="text-xs font-bold text-white whitespace-nowrap">
                            {cat.elevationGain || "-"}M+
                          </p>
                        </div>
                        <div className="h-6 w-px bg-white/20"></div>
                        <div className="text-center">
                          <p className="text-xs text-white/80">CUT OFF</p>
                          <p className="text-xs font-bold text-white whitespace-nowrap">
                            {cat.cot ? `${cat.cot} HOURS` : "- HOURS"}
                          </p>
                        </div>
                        <div className="h-6 w-px bg-white/20"></div>
                        <div className="text-center">
                          <p className="text-xs text-white/80">PRICE</p>
                          <p className="text-xs font-bold text-white whitespace-nowrap">
                            IDR {parsePrice(cat.price).toLocaleString("en-US")}
                          </p>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-bold text-white uppercase">
                          {cat.isRegistrationOpen ? "REGISTRATION OPEN" : "REGISTRATION CLOSED"}
                        </p>
                      </div>
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-lime-600 bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                      {existingRegistration ? (
                        <>
                          <p className="text-sm font-bold uppercase text-white">Already Registered</p>
                          <p className="text-xs text-white/80 mt-1">View My Registration →</p>
                        </>
                      ) : (
                        <p className="text-lg font-normal uppercase text-white">Register Now</p>
                      )}
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Event Info Section ── */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <div className="text-center mb-8">
            <motion.h2
              className="text-2xl font-black uppercase tracking-wider text-lime-600"
              initial={{ opacity: 0, y: -15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              EVENT INFO
            </motion.h2>
            <motion.h3
              className="text-3xl md:text-5xl font-black text-lime-600 mt-2 uppercase"
              initial={{ opacity: 0, y: -15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              About Road to JOTR
            </motion.h3>
          </div>

          <motion.p
            className="text-lg text-gray-600 leading-relaxed mb-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {event.aboutEvent}
          </motion.p>

          <motion.div
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 py-10 px-8 bg-lime-50 rounded-2xl border border-lime-100"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center">
              <h4 className="text-[10px] font-black text-lime-600 mb-2 uppercase tracking-[0.2em]">Location</h4>
              <p className="text-xl font-black text-gray-900 uppercase tracking-tighter">{event.location}</p>
            </div>
            <div className="hidden md:block w-px h-12 bg-lime-200"></div>
            <div className="text-center">
              <h4 className="text-[10px] font-black text-lime-600 mb-2 uppercase tracking-[0.2em]">Race Date</h4>
              <p className="text-xl font-black text-gray-900 uppercase tracking-tighter">June 15, 2026</p>
            </div>
            <div className="hidden md:block w-px h-12 bg-lime-200"></div>
            <div className="text-center">
              <h4 className="text-[10px] font-black text-lime-600 mb-2 uppercase tracking-[0.2em]">Status</h4>
              <p className="text-xl font-black text-lime-600 uppercase tracking-tighter">Registration Open</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
